import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AsyncPaginate, wrapMenuList } from "react-select-async-paginate";
import { components } from "react-select";
import { toast } from "react-toastify";
import { ceil } from "lodash";
import courseApi from "api/courseApi";

interface Option {
  label: string | any;
  value: string;
}
interface DropdownOptionProps {
  name?: string;
  listCheckedCourse?: any | null;
  debounceTimeout?: number;
  onChangeSelect?: (params: Option) => void;
  isMulti?: boolean;
  placeholder?: string;
  isShowIconCheck?: boolean;
  hideSelectedOptions?: boolean;
  isAllOption?: boolean;
  isDisabled?: boolean;
  keywordSearch?: string;
  isNotBelongToAgency?: boolean;
  isClearable?: boolean;
  keySetValue?: string;
  lang?: string;
}
const LIMIT = 30;

const DropdownAsyncCourse = ({
  name = "",
  listCheckedCourse,
  debounceTimeout = 700,
  onChangeSelect,
  isMulti = false,
  placeholder,
  isShowIconCheck = false,
  hideSelectedOptions = true,
  isAllOption = false,
  isDisabled = false,
  keywordSearch = "",
  isNotBelongToAgency = false,
  isClearable = true,
  keySetValue = "",
  lang = "",
}: DropdownOptionProps) => {
  const { t } = useTranslation();
  const [isAllCheckedUsers, setIsAllCheckedUsers] = useState(false);
  const [options, setOptions] = useState([]);
  const [courseSelected, setCourseSelected] = useState<any | null>(null);

  const setOnChangeOption = (item: any) => {
    onChangeSelect && onChangeSelect(item);
  };

  const loadListCourse = async ({
    page,
    limit,
    search,
  }: {
    page: number;
    limit: number;
    search?: string;
  }) => {
    try {
      let params: any = { page, page_size: limit, keyword: search, lang };
      if (keywordSearch) {
        params = {
          page,
          page_size: limit,
          app_name: keywordSearch,
          keyword: search,
          lang,
        };
      }

      const {
        data: { items, pagination },
        statusCode,
      } = await courseApi.courseList(params);

      if (statusCode !== 200) {
        return { success: false };
      }

      const dataUsersOptions = items?.map((a: any) => {
        return {
          value: String(keySetValue ? a?.[keySetValue] : a?.id),
          label: (
            <div className="d-flex align-items-center gap-2">
              <div>{`${a?.name ? a?.name : ""}`}</div>
            </div>
          ),
          data: a,
        };
      });

      return {
        code: statusCode,
        dataUsersOptions: dataUsersOptions || [],
        lastPage: ceil(pagination?.total / pagination?.page_size),
      };
    } catch (err) {
      return {
        code: 404,
        dataUsersOptions: [],
        lastPage: 1,
      };
    }
  };

  const callbackLoadOptions = useCallback(
    async function loadOptions(
      search: string,
      loadedOptions: any[],
      { page }: { page: number }
    ) {
      const { code, dataUsersOptions, lastPage } = await loadListCourse({
        page,
        limit: LIMIT,
        search,
      });
      let dataUsersOptionsFormat = dataUsersOptions || [];

      if (code !== 200) {
        toast(`${t("Failed to load data")}`, CONFIG_OPTION_TOAST_ERROR);
        return {
          options: [],
          hasMore: false,
        };
      }
      let lastPageFormat = lastPage ? lastPage : 1;
      let options: any = dataUsersOptionsFormat;

      if (isAllOption && isNotBelongToAgency) {
        options = [
          ...(page === 1
            ? [
                { label: t("All"), value: "all" },
                { label: t("Not belong to Agency"), value: "not" },
              ]
            : []),
          ...dataUsersOptionsFormat,
        ];
      } else if (isAllOption) {
        options = [
          ...(page === 1 ? [{ label: t("All"), value: "all" }] : []),
          ...dataUsersOptionsFormat,
        ];
      }

      setOptions(options);

      return {
        options: options,
        hasMore: lastPageFormat > page,
        additional: {
          page: page + 1,
        },
      };
    },
    [keywordSearch]
  );

  const MenuList = (props: any) => {
    let childComponent = props?.children;
    if (props?.children?.length > 0) {
      const allOption = props?.children?.filter(
        (item: any) => item?.props?.value === "all"
      );
      let fixedIndex = -1;
      if (allOption) {
        fixedIndex = props?.children.indexOf(allOption?.[0]);
      }
      const fixedElement = props?.children?.[fixedIndex];

      const childComponentFormat = (
        (fixedIndex !== -1 ? props?.children.slice(1) : props?.children) || []
      )?.sort((a: any, b: any) => {
        let pointA = a.props.isSelected === true ? 1 : 0;
        let pointB = b.props.isSelected === true ? 1 : 0;
        return pointB - pointA;
      });

      if (fixedElement) {
        childComponent = [fixedElement, ...childComponentFormat];
      } else {
        childComponent = childComponentFormat;
      }
    }

    return (
      <components.MenuList {...props}>{childComponent}</components.MenuList>
    );
  };

  const OptionFormat = (props: any) => {
    if (props?.isSelected && props?.value === "all") {
      setIsAllCheckedUsers(true);
    } else if (!props?.isSelected && props?.value === "all") {
      setIsAllCheckedUsers(false);
    }

    return (
      <components.Option {...props} style={{ zIndex: 999 }}>
        <div className="d-flex align-items-center gap-2">
          {(props?.isSelected || isAllCheckedUsers) && isShowIconCheck ? (
            <i className="ri-check-line align-middle fs-14" />
          ) : (
            ""
          )}
          {props.data?.label}
        </div>
      </components.Option>
    );
  };

  const MenuListAsync = wrapMenuList(MenuList);

  useEffect(() => {
    if (isMulti) {
      if (listCheckedCourse?.length) {
        const temp = listCheckedCourse?.map((item: any) => ({
          value: String(item?.value),
          label: (
            <div className="d-flex align-items-center gap-2">
              <div>{`${item?.name ? item?.name : ""}`}</div>
            </div>
          ),
          data: item,
        }));
        setCourseSelected(temp);
      }
    } else {
      if (listCheckedCourse) {
        setCourseSelected({
          value: String(listCheckedCourse?.value),
          label: (
            <div className="d-flex align-items-center gap-2">
              <div>{`${
                listCheckedCourse?.name ? listCheckedCourse?.name : ""
              }`}</div>
            </div>
          ),
          data: listCheckedCourse,
        });
      }
    }
  }, [listCheckedCourse]);

  return (
    <AsyncPaginate
      name={name}
      isMulti={isMulti}
      isClearable={isClearable}
      // defaultOptions
      options={options}
      debounceTimeout={debounceTimeout}
      value={courseSelected}
      loadOptions={callbackLoadOptions}
      placeholder={placeholder}
      className="dropdown-status-rounded"
      hideSelectedOptions={false}
      onChange={(e: any) => setOnChangeOption(e)}
      additional={{
        page: 1,
      }}
      components={{
        Option: OptionFormat,
        ...(isMulti ? { MenuList: MenuListAsync } : null),
        // MenuList: MenuListAsync,
      }}
      closeMenuOnSelect={hideSelectedOptions}
      menuPortalTarget={document.body}
      styles={{
        option: (styles: any) => {
          let styleNew = isMulti
            ? {
                ...styles,
                backgroundColor: "white",
                zIndex: 99,
                color: "black",
              }
            : styles;

          return styleNew;
        },
        control: (styles: any, { isFocused }: any) => {
          return {
            ...styles,
            borderColor: "#e9ebec",
            borderRadius: "0.6rem",
            color: "white",
            "&:hover": { borderColor: "#009ef7" },
          };
        },
        multiValue: (styles: any) => {
          return {
            ...styles,
            backgroundColor: "#009ef7",
          };
        },
        multiValueLabel: (styles: any) => ({
          ...styles,
          color: "white !important",
        }),
        menuPortal: (base: any) => ({
          ...base,
          zIndex: 1051,
        }),
      }}
      isDisabled={isDisabled}
    />
  );
};

export default DropdownAsyncCourse;
